import React, { useEffect, useRef, useState } from "react";
import "./ConfirmDeleteButton.css";

interface ConfirmDeleteButtonProps {
  onConfirm: () => Promise<void> | void;
  className: string;
  confirmClassName: string;
  title?: string;
  confirmTitle?: string;
  ariaLabel?: string;
  confirmAriaLabel?: string;
  confirmTimeoutMs?: number;
  stopPropagation?: boolean;
  onArmedChange?: (armed: boolean) => void;
}

const ConfirmDeleteButton: React.FC<ConfirmDeleteButtonProps> = ({
  onConfirm,
  className,
  confirmClassName,
  title = "Delete",
  confirmTitle = "Tap again to delete",
  ariaLabel = "Delete",
  confirmAriaLabel = "Tap again to delete",
  confirmTimeoutMs = 3000,
  stopPropagation = false,
  onArmedChange,
}) => {
  const [armed, setArmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    onArmedChange?.(armed);
  }, [armed, onArmedChange]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const armConfirm = () => {
    setArmed(true);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setArmed(false);
      timeoutRef.current = null;
    }, confirmTimeoutMs);
  };

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (stopPropagation) {
      e.stopPropagation();
    }

    if (submitting) {
      return;
    }

    if (!armed) {
      armConfirm();
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setSubmitting(true);
    try {
      await onConfirm();
      setArmed(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <span className="confirm-delete">
      {armed && !submitting && (
        <span className="confirm-delete-tooltip" aria-hidden="true">
          {confirmTitle}
        </span>
      )}
      <button
        className={`${className} ${armed ? confirmClassName : ""}`.trim()}
        onClick={(e) => {
          void handleClick(e);
        }}
        disabled={submitting}
        title={armed ? confirmTitle : title}
        aria-label={armed ? confirmAriaLabel : ariaLabel}
      >
        <i className={`fas ${submitting ? "fa-spinner fa-spin" : "fa-trash"}`}></i>
      </button>
    </span>
  );
};

export default ConfirmDeleteButton;
